import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../components/AdminNavbar';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import { 
    fetchAdminCatalogs, saveCatalog, deleteCatalog, 
    clearAdminError 
} from '../../store/slices/adminSlice';
import { CATALOG_CATEGORIES, getCategoryByCode, getCategorySvgUrl } from '../../helper/catalogCategories';
import { getCurrencySymbol } from '../../helper/currencyHelper';

const CatalogListView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { 
        authUser, vendorProfile, catalogs, catalogsLoading, error, infoMessage 
    } = useSelector((state) => state.admin);

    const [showCatalogModal, setShowCatalogModal] = useState(false);
    const [catalogForm, setCatalogForm] = useState({ id: null, name: '', description: '', currency: '$', otherCurrency: '', categoryCode: 'other', color: '#607D8B' });
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [copiedCatalogId, setCopiedCatalogId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (authUser?.uid) {
            dispatch(fetchAdminCatalogs(authUser.uid));
        }
    }, [dispatch, authUser]);

    const isPremiumV2 = vendorProfile?.subscription === 'premiumv2';
    const catalogLimitReached = !isPremiumV2 && catalogs.length >= 3;

    const handleOpenCatalogModal = (catalog = null) => {
        dispatch(clearAdminError());
        if (catalog) {
            const cover = catalog.onlinePath || catalog.coverUrl || catalog.coverPath || '';
            setCatalogForm({
                id: catalog.id,
                name: catalog.name || '',
                description: catalog.description || '',
                currency: catalog.currency || '$',
                otherCurrency: catalog.otherCurrency || '',
                categoryCode: catalog.categoryCode || 'other',
                color: catalog.color || '#607D8B',
                coverUrl: cover,
                onlinePath: cover
            });
            setCoverPreview(cover);
        } else {
            const defaultCat = getCategoryByCode('other');
            setCatalogForm({
                id: null,
                name: '',
                description: '',
                currency: '$',
                otherCurrency: '',
                categoryCode: 'other',
                color: defaultCat.defaultColor,
                coverUrl: '',
                onlinePath: ''
            });
            setCoverPreview('');
        }
        setCoverFile(null);
        setShowCatalogModal(true);
    };

    const handleCategoryChange = (code) => {
        const cat = getCategoryByCode(code);
        setCatalogForm(prev => ({
            ...prev,
            categoryCode: code,
            color: cat.defaultColor
        }));
    };

    const handleSaveCatalog = async (e) => {
        e.preventDefault();
        setSaving(true);
        const res = await dispatch(saveCatalog({
            userID: authUser.uid,
            catalogData: catalogForm,
            coverFile
        }));
        setSaving(false);
        if (!res.error) {
            setShowCatalogModal(false);
        }
    };

    const handleDeleteCatalog = async (catalogID, e) => {
        e.stopPropagation();
        if (window.confirm("¿Estás seguro de eliminar este catálogo? Se eliminarán todos sus productos e imágenes.")) {
            await dispatch(deleteCatalog({ userID: authUser.uid, catalogID }));
        }
    };

    const handleCopyCatalogLink = (catalog, e) => {
        e.stopPropagation();
        const link = `${window.location.origin}/c/${authUser.uid}/${catalog.id}`;
        navigator.clipboard.writeText(link);
        setCopiedCatalogId(catalog.id);
        setTimeout(() => setCopiedCatalogId(null), 2000);
    };

    const [openMenuId, setOpenMenuId] = useState(null);

    useEffect(() => {
        const handleGlobalClick = () => setOpenMenuId(null);
        window.addEventListener('click', handleGlobalClick);
        return () => window.removeEventListener('click', handleGlobalClick);
    }, []);

    const filteredCatalogs = catalogs.filter(c => 
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />
            <AdminBreadcrumb />

            <div className="container container-lg">
                {/* Header Banner */}
                <div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-16">
                    <div>
                        <h4 className="fw-bold text-gray-900 mb-4">Gestión de Catálogos</h4>
                        <p className="text-gray-500 text-xs mb-0">
                            Organiza y administra tus catálogos de productos. Plan: <span className="fw-bold text-main-600">{isPremiumV2 ? 'Premium V2 (Ilimitado)' : 'Gratuito (máx 3)'}</span>
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-12 flex-wrap w-100 w-sm-auto">
                        {/* Search input */}
                        <div className="position-relative flex-grow-1 flex-sm-grow-0" style={{ minWidth: '180px' }}>
                            <input 
                                type="text"
                                className="form-control form-control-sm rounded-pill ps-36 pe-16 text-xs bg-white w-100"
                                placeholder="Buscar catálogo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <i className="ph ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-12 text-gray-400 text-sm"></i>
                        </div>

                        <button 
                            type="button" 
                            className="btn btn-main rounded-pill px-20 py-8 fw-bold text-xs flex-align gap-8 shadow-xs ms-auto ms-sm-0"
                            onClick={() => handleOpenCatalogModal()}
                            disabled={catalogLimitReached}
                        >
                            <i className="ph ph-plus-circle text-sm"></i>
                            Nuevo Catálogo
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                        <i className="ph ph-warning-circle text-md"></i>
                        <span>{error}</span>
                    </div>
                )}

                {infoMessage && (
                    <div className="alert alert-success py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                        <i className="ph ph-check-circle text-md"></i>
                        <span>{infoMessage}</span>
                    </div>
                )}

                {/* Catalog Cards Grid */}
                {catalogsLoading ? (
                    <div className="text-center py-60">
                        <div className="spinner-border text-main-600 mb-12" role="status" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                        <p className="text-gray-500 text-xs fw-semibold mb-0">Cargando catálogos...</p>
                    </div>
                ) : filteredCatalogs.length === 0 ? (
                    <div className="bg-white p-40 rounded-24 text-center border shadow-2xs">
                        <i className="ph ph-folder-plus text-4xl text-gray-300 mb-12 d-block"></i>
                        <h6 className="fw-bold text-gray-700 mb-4">
                            {searchTerm ? 'No se encontraron catálogos con la búsqueda' : 'No tienes ningún catálogo aún'}
                        </h6>
                        <p className="text-gray-400 text-xs mb-16">
                            {searchTerm ? 'Intenta con otro término' : 'Crea tu primer catálogo para comenzar a publicar productos.'}
                        </p>
                        {!searchTerm && (
                            <button 
                                type="button" 
                                className="btn btn-main btn-sm rounded-pill px-20 py-8 text-xs fw-bold"
                                onClick={() => handleOpenCatalogModal()}
                            >
                                Crear Primer Catálogo
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="row g-16 g-md-20">
                        {filteredCatalogs.map(catalog => {
                            const category = getCategoryByCode(catalog.categoryCode);
                            const catalogCoverUrl = catalog.onlinePath || catalog.coverUrl || catalog.coverPath || null;

                            return (
                                <div key={catalog.id} className="col-12 col-md-6 col-lg-4 col-xl-3">
                                    <div 
                                        className={`bg-white rounded-16 border shadow-2xs hover-shadow-md transition-2 cursor-pointer h-100 d-flex flex-column position-relative ${
                                            openMenuId === catalog.id ? 'z-3' : 'z-1'
                                        }`}
                                        onClick={() => navigate(`/admin/dashboard/${catalog.id}`)}
                                    >
                                        {/* Clean Cover Image Banner (Perfectly rounded top corners) */}
                                        <div 
                                            className="w-100 position-relative flex-center overflow-hidden bg-gray-100"
                                            style={{ 
                                                height: '140px', 
                                                borderTopLeftRadius: 'inherit', 
                                                borderTopRightRadius: 'inherit' 
                                            }}
                                        >
                                            {catalogCoverUrl ? (
                                                <img 
                                                    src={catalogCoverUrl} 
                                                    alt={catalog.name} 
                                                    className="w-100 h-100 object-fit-cover" 
                                                    style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}
                                                />
                                            ) : (
                                                <div 
                                                    className="w-100 h-100 flex-center"
                                                    style={{ 
                                                        backgroundColor: catalog.color || category.defaultColor,
                                                        borderTopLeftRadius: 'inherit', 
                                                        borderTopRightRadius: 'inherit' 
                                                    }}
                                                >
                                                    <img 
                                                        src={getCategorySvgUrl(catalog.categoryCode)} 
                                                        alt={category.label}
                                                        className="w-56 h-56 object-fit-contain opacity-75"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Content Body */}
                                        <div className="p-16 d-flex flex-column flex-grow-1">
                                            {/* Catalog Name Row + 3-Dot Action Menu */}
                                            <div className="d-flex align-items-center justify-content-between mb-8 gap-8 position-relative">
                                                <div className="d-flex align-items-center gap-8 overflow-hidden">
                                                    {catalogCoverUrl ? (
                                                        <img 
                                                            src={catalogCoverUrl} 
                                                            alt={catalog.name} 
                                                            className="w-24 h-24 rounded-circle object-fit-cover flex-shrink-0 border"
                                                        />
                                                    ) : (
                                                        <span 
                                                            className="w-12 h-12 rounded-circle flex-shrink-0"
                                                            style={{ backgroundColor: catalog.color || category.defaultColor }}
                                                        ></span>
                                                    )}
                                                    <h6 className="fw-bold text-gray-900 text-sm mb-0 text-truncate">{catalog.name}</h6>
                                                </div>

                                                {/* 3-Dot Menu Button & Unclipped Dropdown */}
                                                <div className="position-relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                    <button 
                                                        type="button"
                                                        className="p-0 rounded-circle flex-center border-0 bg-transparent hover-bg-gray-100 flex-shrink-0 transition-2"
                                                        style={{ width: '32px', height: '32px', minWidth: '32px', minHeight: '32px', lineHeight: 1 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuId(openMenuId === catalog.id ? null : catalog.id);
                                                        }}
                                                        title="Opciones"
                                                    >
                                                        <i className="ph ph-dots-three-vertical text-lg text-gray-700"></i>
                                                    </button>

                                                    {openMenuId === catalog.id && (
                                                        <div 
                                                            className="position-absolute end-0 top-100 mt-4 bg-white rounded-12 shadow-lg border z-10 py-4 min-w-160"
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ zIndex: 1050 }}
                                                        >
                                                            <button 
                                                                type="button" 
                                                                className="dropdown-item px-12 py-6 text-xs text-gray-700 flex-align gap-8 hover-bg-gray-50 border-0 bg-transparent w-100 text-start"
                                                                onClick={(e) => {
                                                                    handleCopyCatalogLink(catalog, e);
                                                                    setOpenMenuId(null);
                                                                }}
                                                            >
                                                                <i className={`ph ${copiedCatalogId === catalog.id ? 'ph-check text-success' : 'ph-link text-main-600'} text-sm`}></i>
                                                                {copiedCatalogId === catalog.id ? 'Copiado!' : 'Copiar enlace'}
                                                            </button>

                                                            <button 
                                                                type="button" 
                                                                className="dropdown-item px-12 py-6 text-xs text-gray-700 flex-align gap-8 hover-bg-gray-50 border-0 bg-transparent w-100 text-start"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenMenuId(null);
                                                                    handleOpenCatalogModal(catalog);
                                                                }}
                                                            >
                                                                <i className="ph ph-pencil-simple text-sm text-gray-600"></i>
                                                                Editar
                                                            </button>

                                                            <div className="dropdown-divider my-4"></div>

                                                            <button 
                                                                type="button" 
                                                                className="dropdown-item px-12 py-6 text-xs text-danger flex-align gap-8 hover-bg-danger-50 border-0 bg-transparent w-100 text-start"
                                                                onClick={(e) => {
                                                                    setOpenMenuId(null);
                                                                    handleDeleteCatalog(catalog.id, e);
                                                                }}
                                                            >
                                                                <i className="ph ph-trash text-sm"></i>
                                                                Eliminar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <p className="text-gray-500 text-2xs mb-12 flex-grow-1 line-clamp-2">
                                                {catalog.description || 'Sin descripción'}
                                            </p>

                                            {/* Bottom Info Row with Category Label */}
                                            <div className="d-flex align-items-center justify-content-between pt-12 border-top text-xs text-gray-500 mt-auto">
                                                <div className="d-flex align-items-center gap-6">
                                                    <span>Moneda: <strong className="text-gray-900">{getCurrencySymbol(catalog)}</strong></span>
                                                </div>
                                                <span className="badge bg-gray-100 text-gray-700 rounded-pill px-10 py-4 text-3xs border">
                                                    {category.label}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Catalog Form Modal */}
            {showCatalogModal && (
                <div className="modal fade show d-block bg-black-50 z-5" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                        <div className="modal-content rounded-24 border-0 shadow-lg overflow-hidden">
                            <div className="modal-header border-bottom p-20 bg-gray-50 flex-between">
                                <h6 className="modal-title fw-bold text-gray-900 mb-0">
                                    {catalogForm.id ? 'Editar Catálogo' : 'Nuevo Catálogo'}
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowCatalogModal(false)}></button>
                            </div>

                            <form onSubmit={handleSaveCatalog}>
                                <div className="modal-body p-24">
                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Categoría Predefinida</label>
                                        <div className="row g-8 max-h-160 overflow-y-auto p-4 border rounded-12">
                                            {CATALOG_CATEGORIES.map(cat => (
                                                <div key={cat.code} className="col-6">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCategoryChange(cat.code)}
                                                        className={`btn btn-sm w-100 text-start p-8 rounded-8 d-flex align-items-center gap-8 border ${
                                                            catalogForm.categoryCode === cat.code ? 'border-main-600 bg-main-25 fw-bold' : 'border-gray-200 bg-white'
                                                        }`}
                                                    >
                                                        <img src={getCategorySvgUrl(cat.code)} alt={cat.label} className="w-16 h-16 object-fit-contain" />
                                                        <span className="text-xs text-truncate">{cat.label}</span>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="row g-16 mb-16">
                                        <div className="col-md-8">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Nombre del Catálogo *</label>
                                            <input 
                                                type="text" 
                                                className="form-control rounded-12 py-10 px-16 text-sm"
                                                placeholder="Ej: Colección Verano 2026"
                                                value={catalogForm.name}
                                                onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Color Primario</label>
                                            <div className="d-flex align-items-center gap-8">
                                                <input 
                                                    type="color" 
                                                    className="form-control form-control-color rounded-8 w-44 h-40 p-2 cursor-pointer"
                                                    value={catalogForm.color}
                                                    onChange={(e) => setCatalogForm({ ...catalogForm, color: e.target.value })}
                                                />
                                                <span className="text-2xs font-monospace text-gray-500">{catalogForm.color}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-16 mb-16">
                                        <div className="col-md-6">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Moneda</label>
                                            <select 
                                                className="form-select rounded-12 py-10 px-16 text-sm"
                                                value={catalogForm.currency}
                                                onChange={(e) => setCatalogForm({ ...catalogForm, currency: e.target.value })}
                                            >
                                                <option value="$">$ (Dólar / Genérico)</option>
                                                <option value="Q">Q (Quetzal Guatemala)</option>
                                                <option value="€">€ (Euro)</option>
                                                <option value="S/">S/ (Sol Peruano)</option>
                                                <option value="L">L (Lempira Honduras)</option>
                                                <option value="other">Otro (Personalizado)</option>
                                            </select>
                                        </div>

                                        {catalogForm.currency === 'other' && (
                                            <div className="col-md-6">
                                                <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Símbolo Personalizado *</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control rounded-12 py-10 px-16 text-sm"
                                                    placeholder="Ej: MX$, C$"
                                                    value={catalogForm.otherCurrency}
                                                    onChange={(e) => setCatalogForm({ ...catalogForm, otherCurrency: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Descripción Corta</label>
                                        <textarea 
                                            className="form-control rounded-12 py-10 px-16 text-sm"
                                            rows="2"
                                            placeholder="Descripción o términos generales del catálogo..."
                                            value={catalogForm.description}
                                            onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })}
                                        ></textarea>
                                    </div>

                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Imagen de Portada</label>
                                        <input 
                                            type="file" 
                                            accept="image/*"
                                            className="form-control rounded-12 text-sm"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    setCoverFile(file);
                                                    setCoverPreview(URL.createObjectURL(file));
                                                }
                                            }}
                                        />
                                        {coverPreview && (
                                            <img src={coverPreview} alt="Cover preview" className="w-100 h-100 rounded-12 object-fit-cover border mt-8" style={{ maxHeight: '120px' }} />
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer p-16 border-top bg-gray-50 flex-between">
                                    <button type="button" className="btn btn-outline-gray-200 rounded-pill px-20 text-xs fw-semibold" onClick={() => setShowCatalogModal(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn btn-main rounded-pill px-24 py-10 text-xs fw-bold" disabled={saving}>
                                        {saving ? 'Guardando...' : 'Guardar Catálogo'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CatalogListView;
